import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { type Task, task } from 'ember-concurrency';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type HotelSettingsMyHotelHotelDetailsController from 'lumen/controllers/hotel/settings/my-hotel/hotel-details';
import type { SubscriptionProfile } from 'lumen/models';
import type { SubscriptionProfilePayload } from 'lumen/models/subscription/profile';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { Transition } from 'lumen/services/transition';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { UNIFIED_SETTINGS_FEATURES } from 'lumen/utils/constants/unified-settings-feature-constants';
import protectedRoute from 'lumen/utils/decorators/protected-route';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import {
  setupUnsavedChangesTransitionHandler,
  type TransitionHandler,
} from 'lumen/utils/unsaved-changes-transition-handlers';

export type HotelDetailsModel = {
  requestProfileTask: Task<SubscriptionProfile, []>;
};

// @ts-expect-error: Unable to resolve signature of class decorator when called as an expression.
@protectedRoute({
  featureToggles: FEATURE_TOGGLES.SETTINGS_UNIFIED_SETTINGS,
  features: [
    UNIFIED_SETTINGS_FEATURES.SUBSCRIPTION_PROFILE_VIEW,
    UNIFIED_SETTINGS_FEATURES.SUBSCRIPTION_PROFILE_EDIT,
    UNIFIED_SETTINGS_FEATURES.CHANNEL_MANAGER,
  ],
  redirectTo: 'hotel.settings',
})
export default class HotelSettingsMyHotelHotelDetailsRoute extends Route {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  #transitionHandler?: TransitionHandler;

  requestProfileTask: Task<SubscriptionProfile, []> = task(async () => {
    try {
      const subscriptionId = this.hotelAndMarketService.hotel.id!;

      const url = formatUrl(
        `/${APIGATEWAY}/configure/subscription/profile`,
        { subscription_id: subscriptionId },
        this.ajax.host,
      );
      const response = await this.ajax.request<SubscriptionProfilePayload>(
        url,
        { method: 'GET' },
      );
      const result = pushAndReturnPayload(
        this.store,
        { 'subscription-profiles': [{ ...response, id: subscriptionId }] },
        'subscription/profile',
      );

      return (result['subscription-profiles'] as SubscriptionProfile[])[0]!;
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  deactivate(): void {
    this.#transitionHandler?.cleanup();
    this.#transitionHandler = undefined;

    void this.requestProfileTask.cancelAll();

    if (this.store.isDestroyed) {
      return;
    }

    this.store.unloadAll('subscription/profile');
    this.store.unloadAll('subscription/channel-distribution');
  }

  model(): HotelDetailsModel {
    void this.requestProfileTask.perform();

    return {
      requestProfileTask: this.requestProfileTask,
    };
  }

  resetController(
    controller: HotelSettingsMyHotelHotelDetailsController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (isExiting && this.#transitionHandler) {
      this.#transitionHandler.cleanup();
      this.#transitionHandler = undefined;
    }
  }

  setupController(
    controller: HotelSettingsMyHotelHotelDetailsController,
    model: HotelDetailsModel,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);
    this.#transitionHandler?.cleanup();
    this.#transitionHandler = setupUnsavedChangesTransitionHandler(
      this.router,
      controller,
      'hotel.settings.my-hotel.hotel-details',
    );
  }
}
